import React, { useState, useEffect } from 'react';
import { getTranslation, getRandomTopicExample, getKnowledgeLevels } from './languages';
import { getTimezoneOptions } from './ai/enums';
import { useTimezone } from './contexts/TimezoneContext';

const learningPurposes = [
    { id: 'hobby', label: 'For Interest / Hobby' },
    { id: 'professional', label: 'For Technical / Professional Growth' }
];

const languageOptions = [
    { id: 'en', label: 'English' },
    { id: 'zh-CN', label: 'Simplified Chinese (简体中文)' },
    { id: 'zh-Hant', label: 'Traditional Chinese (繁體中文)' },
    { id: 'ja', label: 'Japanese (日本語)' }
];

// Map UI language codes to language option IDs
const getDefaultLanguage = (uiLanguage) => {
    switch (uiLanguage) {
        case 'zh-CN':
            return 'zh-CN';
        case 'zh-Hant':
            return 'zh-Hant';
        case 'ja':
            return 'ja';
        case 'en':
        default:
            return 'en';
    }
};

export default function AddTopicModal({ isOpen, onClose, onAddTopic, uiLanguage = 'en', topicGroups = [] }) {
    const [topicName, setTopicName] = useState('');
    const [userScale, setUserScale] = useState(null);
    const [purpose, setPurpose] = useState(null);
    const [language, setLanguage] = useState(getDefaultLanguage(uiLanguage));
    const [description, setDescription] = useState('');
    const [topicExample, setTopicExample] = useState(null);
    const [isScheduled, setIsScheduled] = useState(false);
    const [scheduleTimeHour, setScheduleTimeHour] = useState(7);
    const [scheduleTimePeriod, setScheduleTimePeriod] = useState(0); // 0 = AM, 1 = PM
    const [scheduleFrequencyValue, setScheduleFrequencyValue] = useState(1);
    const [scheduleFrequencyUnit, setScheduleFrequencyUnit] = useState(1); // 0 = hours, 1 = days, 2 = weeks, 3 = months (default to days)
    const [scheduledSkipIfUnread, setScheduledSkipIfUnread] = useState(false);
    const { timezone } = useTimezone();
    const [userTimezone, setUserTimezone] = useState(timezone);
    const [activeTab, setActiveTab] = useState(0);
    const [selectedGroupId, setSelectedGroupId] = useState(null);

    const t = (key) => getTranslation(key, uiLanguage);
    
    // Tab configuration
    const tabs = [
        { id: 0, label: t('learningDetails') || 'Learning Details', icon: '📚' },
        { id: 1, label: t('knowledgeContext') || 'Knowledge & Context', icon: '🧠' },
        { id: 2, label: t('settings') || 'Settings', icon: '⚙️' }
    ];
    
    // Get translated knowledge levels
    const getTranslatedKnowledgeLevels = () => {
        const levels = [];
        const knowledgeLevels = getKnowledgeLevels(uiLanguage);
        for (let i = 0; i <= 10; i++) {
            const levelData = knowledgeLevels[i.toString()];
            if (levelData) {
                levels.push({
                    scale: i,
                    label: levelData.label,
                    description: levelData.description,
                    hobbyDescription: levelData.hobbyDescription,
                    professionalDescription: levelData.professionalDescription
                });
            }
        }
        return levels;
    };
    
    const translatedKnowledgeLevels = getTranslatedKnowledgeLevels();
    const currentLevel = translatedKnowledgeLevels.find(l => l.scale === userScale) || {};
    
    // Get the appropriate description based on the selected purpose
    const getCurrentDescription = () => {
        if (userScale === null) return t('selectKnowledgeLevel') || 'Please select your knowledge level';
        if (!currentLevel) return '';
        const desc = purpose === 'professional' ? currentLevel.professionalDescription : currentLevel.hobbyDescription;
        return desc;
    };

    // Get a random topic example when modal opens
    useEffect(() => {
        if (isOpen) {
            // Reset form to default values whenever it's opened
            setTopicName('');
            setUserScale(null);
            setPurpose(null);
            setLanguage(getDefaultLanguage(uiLanguage));
            setDescription('');
            setTopicExample(getRandomTopicExample(uiLanguage));
            setIsScheduled(false);
            setScheduleTimeHour(7);
            setScheduleTimePeriod(0);
            setScheduleFrequencyValue(1);
            setScheduleFrequencyUnit(1);
            setScheduledSkipIfUnread(false);
            setUserTimezone(timezone); // Always use current browser timezone
            setActiveTab(0); // Always start with the first tab
            setSelectedGroupId(null); // Reset to ungrouped
        }
    }, [isOpen, uiLanguage, timezone]);

    if (!isOpen) return null;

    // Check if all required fields are filled
    const isFormValid = topicName.trim() && purpose !== null && userScale !== null;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (isFormValid) {
            onAddTopic(topicName.trim(), userScale, purpose, language, description.trim(), isScheduled, scheduleTimeHour, scheduleTimePeriod, scheduleFrequencyValue, scheduleFrequencyUnit, scheduledSkipIfUnread, userTimezone, selectedGroupId);
        }
    };

    const handleBackdropClick = (e) => {
        // Only close if clicking on the backdrop itself, not the modal content
        if (e.target === e.currentTarget) {
            onClose();
        } else {
        }
    };

    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4"
            onMouseDown={handleBackdropClick}
        >
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl animate-fade-in-up">
                <h2 className="text-xl font-semibold mb-4 text-gray-800">{t('startNewLearningTopic')}</h2>
                {/* Tab Navigation */}
                <div className="flex border-b border-gray-200 mb-6">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === tab.id
                                    ? 'border-sky-500 text-sky-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <span>{tab.icon}</span>
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>

                <form onSubmit={handleSubmit}>
                    
                    {/* Tab Content */}
                    {activeTab === 0 && (
                        <div className="space-y-6">
                            {/* Topic Name */}
                            <div>
                                <label className="text-sm font-bold text-gray-600 block mb-2">
                                    {t('topicName')}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    autoFocus
                                    value={topicName}
                                    onChange={(e) => setTopicName(e.target.value)}
                                    placeholder={topicExample ? `e.g., ${topicExample.name}` : t('topicNamePlaceholder')}
                                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                                    autoComplete="off"
                                />
                            </div>

                            {/* Learning Goal */}
                            <div>
                                <label className="text-sm font-bold text-gray-600 block mb-3">
                                    {t('learningGoalQuestion')}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <div className="flex gap-4">
                                    {learningPurposes.map(p => (
                                        <button
                                            key={p.id}
                                            type="button"
                                            onClick={() => setPurpose(p.id)}
                                            className={`w-full p-3 rounded-md border text-sm transition-colors ${purpose === p.id ? 'bg-sky-500 text-white border-sky-500' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                                        >
                                            {t(p.id)}
                                        </button>
                                    ))}
                                </div>
                                {purpose === null && (
                                    <p className="text-sm text-gray-500 mt-2">{t('selectLearningGoal') || 'Please select your learning goal'}</p>
                                )}
                            </div>

                            {/* Language Selection */}
                            <div>
                                <label className="text-sm font-bold text-gray-600 block mb-2">{t('contentLanguageQuestion')}</label>
                                <select
                                    value={language}
                                    onChange={(e) => setLanguage(e.target.value)}
                                    className="w-full p-3 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                                >
                                    {languageOptions.map(lang => (
                                        <option key={lang.id} value={lang.id}>{lang.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Topic Group Selection */}
                            <div>
                                <label className="text-sm font-bold text-gray-600 block mb-2">{t('topicGroup') || 'Topic Group'}</label>
                                <select
                                    value={selectedGroupId || ''}
                                    onChange={(e) => setSelectedGroupId(e.target.value || null)}
                                    className="w-full p-3 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                                >
                                    <option value="">{t('ungrouped') || 'Ungrouped'}</option>
                                    {topicGroups.map(group => (
                                        <option key={group.id} value={group.id}>{group.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    {activeTab === 1 && (
                        <div className="space-y-6">
                            {/* Knowledge Scale */}
                            <div>
                                <label className="text-sm font-bold text-gray-600 block mb-3">
                                    {t('rateYourKnowledge')}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="range"
                                    min="0" max="10" step="1"
                                    value={userScale || 0}
                                    onChange={(e) => setUserScale(Number(e.target.value))}
                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                />
                                <div className="flex justify-between text-xs text-gray-500 px-1 mt-1">
                                    {Array.from(Array(11).keys()).map(i => <span key={i}>{i}</span>)}
                                </div>
                                <div className="mt-3 text-center p-5 bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 rounded-xl border-2 border-dashed border-gray-300 min-h-[80px] flex flex-col justify-center transition-all duration-200 hover:border-sky-300 hover:bg-gradient-to-br hover:from-sky-50 hover:via-blue-50 hover:to-indigo-50">
                                    {userScale !== null ? (
                                        <>
                                            <span className="font-bold text-sky-800 text-base">{currentLevel.label} (Level {currentLevel.scale})</span>
                                            <p className="text-sm text-sky-700 mt-1">{getCurrentDescription()}</p>
                                        </>
                                    ) : (
                                        <p className="text-base text-gray-600 font-medium leading-relaxed">{getCurrentDescription()}</p>
                                    )}
                                </div>
                            </div>

                            {/* Learning Context & Goals */}
                            <div>
                                <label className="text-sm font-bold text-gray-600 block mb-2">{t('learningContext')}</label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows="8"
                                    maxLength={500}
                                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
                                    placeholder={t('learningContextPlaceholder') || 'Describe your specific learning goals, interests, or what you want to focus on...'}
                                />
                                <div className="flex justify-between items-center mt-1">
                                    <p className="text-xs text-gray-500">
                                        {t('learningContextHelp')}
                                    </p>
                                    <span className={`text-xs ${description.length >= 450 ? 'text-orange-600' : 'text-gray-500'}`}>
                                        {description.length}/500
                                    </span>
                                </div>
                                {topicExample && (
                                    <div className="text-xs text-gray-400 mt-2 p-2 bg-gray-50 rounded border">
                                        <strong>{t('example')}:</strong> "{topicExample.description}"
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 2 && (
                        <div className="space-y-6">
                            {/* Scheduling Controls */}
                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center gap-3 mb-4">
                                    <input
                                        type="checkbox"
                                        id="isScheduled"
                                        checked={isScheduled}
                                        onChange={(e) => setIsScheduled(e.target.checked)}
                                        className="h-5 w-5 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="isScheduled" className="text-sm font-bold text-gray-700 cursor-pointer">
                                        {t('enableAutomaticTipGeneration')}
                                    </label>
                                </div>
                                
                                {isScheduled && (
                                    <div className="ml-8 space-y-4">
                                        {/* Time of day */}
                                        <div>
                                            <label className="text-sm font-medium text-gray-600 block mb-2">
                                                {t('generateTipsAt')}
                                            </label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="12"
                                                    value={scheduleTimeHour}
                                                    onChange={(e) => setScheduleTimeHour(Math.max(1, Math.min(12, parseInt(e.target.value) || 4)))}
                                                    className="w-16 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                                                />
                                                <select
                                                    value={scheduleTimePeriod}
                                                    onChange={(e) => setScheduleTimePeriod(parseInt(e.target.value))}
                                                    className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                                                >
                                                    <option value={0}>AM</option>
                                                    <option value={1}>PM</option>
                                                </select>
                                            </div>
                                        </div>
                                        
                                        {/* Frequency */}
                                        <div>
                                            <label className="text-sm font-medium text-gray-600 block mb-2">
                                                {t('generateNewTipsEvery')}
                                            </label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="30"
                                                    value={scheduleFrequencyValue}
                                                    onChange={(e) => setScheduleFrequencyValue(Math.max(1, Math.min(30, parseInt(e.target.value) || 1)))}
                                                    className="w-16 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                                                />
                                                <select
                                                    value={scheduleFrequencyUnit}
                                                    onChange={(e) => setScheduleFrequencyUnit(parseInt(e.target.value))}
                                                    className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                                                >
                                                    {process.env.NODE_ENV !== 'production' && (
                                                        <option value={0}>{t('hours')}</option>
                                                    )}
                                                    <option value={1}>{t('days')}</option>
                                                    <option value={2}>{t('weeks')}</option>
                                                    <option value={3}>{t('months')}</option>
                                                </select>
                                            </div>
                                        </div>
                                        
                                        {/* Timezone Selection */}
                                        <div>
                                            <label className="text-sm font-medium text-gray-600 block mb-2">
                                                {t('timezone')}
                                            </label>
                                            <select
                                                value={userTimezone}
                                                onChange={(e) => setUserTimezone(parseInt(e.target.value))}
                                                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                                            >
                                                {getTimezoneOptions(uiLanguage).map(timezone => (
                                                    <option key={timezone.value} value={timezone.value}>
                                                        {timezone.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        
                                        <p className="text-xs text-gray-500">
                                            {t('schedulingNote')}
                                        </p>
                                        <div className="flex items-center gap-2 mt-4">
                                            <input
                                                type="checkbox"
                                                id="scheduledSkipIfUnread"
                                                checked={scheduledSkipIfUnread}
                                                onChange={e => setScheduledSkipIfUnread(e.target.checked)}
                                                className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor="scheduledSkipIfUnread" className="text-xs text-gray-700 cursor-pointer">
                                                {t('scheduledSkipIfUnread')}
                                            </label>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Tab Navigation Buttons */}
                    <div className="flex justify-between items-center mt-6">
                        <div className="flex gap-2">
                            {activeTab > 0 && (
                                <button 
                                    type="button" 
                                    onClick={() => setActiveTab(activeTab - 1)}
                                    className="px-4 py-2 rounded-md text-gray-600 bg-gray-100 hover:bg-gray-200 flex items-center gap-2"
                                >
                                    ← {t('previous') || 'Previous'}
                                </button>
                            )}
                            {activeTab < tabs.length - 1 && (
                                <button 
                                    type="button" 
                                    onClick={() => setActiveTab(activeTab + 1)}
                                    className="px-4 py-2 rounded-md text-white bg-sky-500 hover:bg-sky-600 flex items-center gap-2"
                                >
                                    {t('next') || 'Next'} →
                                </button>
                            )}
                        </div>
                        <div className="flex gap-4">
                            <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-gray-600 bg-gray-100 hover:bg-gray-200">{t('cancel')}</button>
                            <div className="relative">
                                <button 
                                    type="submit" 
                                    disabled={!isFormValid} 
                                    className="px-4 py-2 rounded-md text-white bg-sky-500 hover:bg-sky-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                    title={!isFormValid ? (t('completeAllFieldsToStart') || 'Please complete the topic name, learning goal, and knowledge level to start learning') : ''}
                                >
                                    {t('startLearning')}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}